import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties } from 'react';
import { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { type DropCardInput } from '@sorare/core/src/__generated__/globalTypes';
import { AnimatedNumber } from '@sorare/core/src/atoms/animations/AnimatedNumber/index.tsx';
import { SorareLogo } from '@sorare/core/src/atoms/icons/SorareLogo/index.tsx';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandT } from '@sorare/core/src/atoms/typography';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg/index.tsx';
import { Dialog } from '@sorare/core/src/components/dialog';
import { sportProfile } from '@sorare/core/src/contexts/currentUser/queries';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useImagePrefetcher } from '@sorare/core/src/hooks/useImagePrefetcher.ts';
import { CARD_SIZE } from '@sorare/core/src/lib/cards';
import type { ColorFormat } from '@sorare/core/src/lib/colors';
import { useEvents } from '@sorare/core/src/lib/events/useEvents.ts';
import { ClosePackRevealDialog_PackDialogStep } from '@sorare/core/src/protos/events/platform/web/events.ts';
import { useGetCardSetConfig } from '@sorare/core/src/set';
import { SetColorTheme } from '@sorare/core/src/set/SetColorTheme/index.tsx';

import { TinderCardSwipe } from '#src/components/commonCard/TinderCardSwipe/index.tsx';

import { PackTear } from './PackTear/index.tsx';
import type {
  RevealPackCardsDialog_anyCardInterface,
  claimPackCardsOnCloseMutation,
  claimPackCardsOnCloseMutationVariables,
} from './__generated__/index.graphql.ts';

const Root = styled.div`
  display: grid;
  width: 100%;
  position: relative;
  isolation: isolate;
  flex: 1;
  min-height: 100%;
  overflow: visible;
`;

const TinderCardSwipeWrapper = styled(Vertical)`
  width: 100%;
  flex: 1;
  &.hidden {
    opacity: 0;
    position: absolute;
    inset: 0;
    pointer-events: none;
  }
`;
const PackTearWrapper = styled.div`
  height: 100%;
  width: 100%;
  flex: 1;
`;
const Header = styled(Vertical)`
  padding: var(--double-and-a-half-unit) 0;
`;

const CLAIM_PACK_CARDS_ON_CLOSE_MUTATION = gql`
  mutation claimPackCardsOnCloseMutation(
    $input: claimCardsFromPackInput!
    $sport: Sport!
  ) {
    claimCardsFromPack(input: $input) {
      currentUser {
        slug
        ...CurrentUseProvider_sportProfile
      }
      pack {
        id
        claimed
      }
      errors {
        message
        code
      }
    }
  }
  ${sportProfile}
` as TypedDocumentNode<
  claimPackCardsOnCloseMutation,
  claimPackCardsOnCloseMutationVariables
>;

type Props = {
  open: boolean;
  cards: RevealPackCardsDialog_anyCardInterface[];
  onContinue: () => void;
  packId: string;
  packPictureUrl: string;
  isFirstPack?: boolean;
  initialPackAnimationDone?: boolean;
  primaryColor: ColorFormat;
  secondaryColor: ColorFormat;
  showPlayerDetails?: boolean;
  cardSetSlug: string;
  onCardsClaimed?: () => void;
};

export const RevealPackCardsDialog = ({
  open,
  cards,
  onContinue,
  isFirstPack,
  packId,
  packPictureUrl,
  initialPackAnimationDone = false,
  showPlayerDetails,
  primaryColor,
  secondaryColor,
  cardSetSlug,
  onCardsClaimed,
}: Props) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const getCardSetConfig = useGetCardSetConfig();
  const { visual } = getCardSetConfig(cardSetSlug);
  const { background } = visual.cardReveal;
  const { SetIcon } = visual.brand;

  const [packAnimationDone, setPackAnimationDone] = useState(
    initialPackAnimationDone
  );
  const isLaptopAndAbove = useIsLaptop();

  const [displayRecap, setDisplayRecap] = useState(false);

  const [droppedCards, setDroppedCards] = useState<DropCardInput[]>([]);
  const packImageLoaded = useImagePrefetcher([
    proxyUrl(packPictureUrl, {
      cropWidth: CARD_SIZE,
    }),
  ]);

  const track = useEvents();
  const { sport } = useSportContext();
  const [claimPackCardsOnClose] = useMutation(
    CLAIM_PACK_CARDS_ON_CLOSE_MUTATION,
    { showErrorsWithSnackNotification: true }
  );
  const [cardsClaimed, setCardsClaimed] = useState(false);
  const closingRef = useRef(false);

  const handleCardsClaimed = () => {
    setCardsClaimed(true);
    onCardsClaimed?.();
  };

  const handleEscape = async () => {
    if (!cards.length || closingRef.current) {
      return;
    }
    if (!displayRecap && !cardsClaimed) {
      setPackAnimationDone(true);
      setDisplayRecap(true);
      return;
    }
    closingRef.current = true;
    track('Close Pack Reveal Dialog', {
      step: cardsClaimed
        ? ClosePackRevealDialog_PackDialogStep.COLLECTION_ANIMATION
        : ClosePackRevealDialog_PackDialogStep.RECAP,
    });
    if (!cardsClaimed && packId) {
      try {
        const { success } = await claimPackCardsOnClose({
          variables: {
            sport,
            input: {
              packId,
              chosenCardSlugs: cards
                .map(card => card.slug)
                .filter(slug =>
                  droppedCards.every(
                    droppedCard => droppedCard.cardSlug !== slug
                  )
                ),
            },
          },
        });
        if (success) {
          onCardsClaimed?.();
        }
      } finally {
        onContinue();
      }
      return;
    }
    onContinue();
  };

  return (
    <Dialog
      open={open}
      onClose={handleEscape}
      hideHeader
      fullWidth
      maxWidth="xs"
      disableBackdropClick
      fullScreen={!isLaptopAndAbove}
    >
      <Root
        style={
          {
            ...background,
            '--primary-color': primaryColor,
            '--secondary-color': secondaryColor,
          } as unknown as CSSProperties
        }
      >
        <TinderCardSwipeWrapper
          className={classNames({ hidden: !packAnimationDone })}
        >
          <TinderCardSwipe
            cards={cards}
            cardSetSlug={cardSetSlug}
            onContinue={onContinue}
            isFirstPack={isFirstPack}
            packId={packId}
            droppedCards={droppedCards}
            setDroppedCards={setDroppedCards}
            packAnimationDone={packAnimationDone}
            showPlayerDetails={showPlayerDetails}
            displayRecap={displayRecap}
            setDisplayRecap={setDisplayRecap}
            onCardsClaimed={handleCardsClaimed}
            onNextCard={setCurrentIndex}
            header={
              <SetColorTheme setSlug={cardSetSlug}>
                <Header center gap={!SetIcon ? 3 : 2}>
                  {!SetIcon ? (
                    <SorareLogo color="var(--c-white)" size="mediumLarge" />
                  ) : (
                    <SetIcon color="var(--c-white)" size={5} />
                  )}
                  <BrandT color="var(--c-white)">
                    <FormattedMessage
                      defaultMessage="Your cards: {animatedNumber} / {total}"
                      values={{
                        animatedNumber: (
                          <AnimatedNumber value={currentIndex + 1} />
                        ),
                        total: cards.length,
                      }}
                    />
                  </BrandT>
                </Header>
              </SetColorTheme>
            }
          />
        </TinderCardSwipeWrapper>
        {!packAnimationDone && (
          <PackTearWrapper>
            <PackTear
              onDone={() => {
                setPackAnimationDone(true);
              }}
              firstCardUrl={cards.at(0)?.pictureUrl || ''}
              loading={!cards.length || !packImageLoaded}
              packPictureUrl={packPictureUrl}
              header={
                <SetColorTheme setSlug={cardSetSlug}>
                  <Header center gap={isFirstPack || !SetIcon ? 3 : 2}>
                    {isFirstPack || !SetIcon ? (
                      <SorareLogo color="var(--c-white)" size="mediumLarge" />
                    ) : (
                      <SetIcon color="var(--c-white)" size={5} />
                    )}
                    <BrandT color="var(--c-white)">
                      <FormattedMessage defaultMessage="Your pack is ready!" />
                    </BrandT>
                  </Header>
                </SetColorTheme>
              }
            />
          </PackTearWrapper>
        )}
      </Root>
    </Dialog>
  );
};

RevealPackCardsDialog.fragments = {
  anyCardInterface: gql`
    fragment RevealPackCardsDialog_anyCardInterface on AnyCardInterface {
      slug
      ...TinderCardSwipe_anyCardInterface
    }
    ${TinderCardSwipe.fragments.anyCardInterface}
  ` as TypedDocumentNode<RevealPackCardsDialog_anyCardInterface>,
};
